import { ReactElement, useMemo } from 'react'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { FactsheetExportMenu } from 'src/shared/menu/FactsheetExportMenu'
import { useParams } from 'react-router'
import { useUserLocale } from 'src/user/UserContext'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { usePermissionsForFactsheetCommittees } from 'src/service/security/PermissionHook'
import { EditButton } from 'src/shared/button/Buttons'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { ROUTES } from 'src/routing/routes'
import { Card, CardContent, CardHeader, ListItem, ListItemIcon, ListItemText, Stack } from '@mui/material'
import { PROCESS } from 'src/shared/constants/constants'
import { ReadOnlySelection, ReadOnlyTextField } from 'src/shared/presentation/ReadOnly'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { S } from 'src/shared/styled/S'
import { CheckIcon } from 'src/shared/icons/Icons'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { useResolveFactsheetCommitteeData } from 'src/screens/factsheets/application/committee/common/FactsheetCommitteeService'

export const FactsheetApplicationCommitteePage = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const { factsheetId } = useParams()
  const factsheet_id = parseInt(factsheetId as string)
  const locale = useUserLocale()
  const navigate = useDelayedNavigate()

  const {
    loading,
    canView,
    canEdit,
    metadata: { factsheetLevel },
  } = usePermissionsForFactsheetCommittees(factsheet_id)

  const { initialFormValues } = useResolveFactsheetCommitteeData(
    factsheet_id,
    PROCESS.FACTSHEET,
    factsheetLevel || undefined,
    false,
  )

  const pageData = useMemo(() => {
    return initialFormValues
      ? {
          committeeConfigs: initialFormValues.committees,
          applicationDecision: initialFormValues.factsheetApplicationDecision,
          qualityCriteria: initialFormValues.factsheetApplicationDecision.qualityCriteriaIds,
          exclusionCriteria: initialFormValues.factsheetApplicationDecision.exclusionCriteriaIds,
          gfchResponsible: initialFormValues.factsheetApplicationDecision.gfchResponsibleValue,
        }
      : null
  }, [initialFormValues])

  const handleEditButtonClick = () => {
    navigate(ROUTES.FactsheetApplicationCommitteeEdit.params({ factsheetId }))
  }

  if (!loading && !canView) {
    return <NotAuthorized />
  }

  return (
    <ScreenLayout
      title={getMessage('label.navigation.factsheets.application.committees')}
      actions={<FactsheetExportMenu />}
      hasSecondLevelNavigation={false}
    >
      <PageLayout>
        {!loading && !!pageData && (
          <Section
            id="tbd-committee-1"
            title={getMessage('label.title.application.committees')}
            actionButton={<EditButton hidden={!canEdit} onClick={handleEditButtonClick} />}
            helpAndInstructions={<HelpAndInstructions labelKey={`label.help.committees.factsheet`} />}
          >
            <Stack spacing={5} mt={2}>
              {/* committee configs for factsheet */}
              {pageData.committeeConfigs?.map((committeeConfigData: any) => (
                <Card key={committeeConfigData.code}>
                  <CardHeader title={getMessage(committeeConfigData.label[locale])} />
                  <CardContent>
                    <ReadOnlyTextField text={getMessage('label.committee.notes')}>
                      {committeeConfigData.description &&
                        !HtmlSanitizer.isEmptyLineHtmlParagraph(committeeConfigData.description) && (
                          <HtmlRenderer html={committeeConfigData.description} />
                        )}
                    </ReadOnlyTextField>
                    <ReadOnlySelection text={getMessage('label.recommendation')} isLast={true}>
                      {committeeConfigData.recommendation &&
                        getMessage(`label.recommendation.${committeeConfigData.recommendation}`)}
                    </ReadOnlySelection>
                  </CardContent>
                </Card>
              ))}

              {/* pros and cons committee */}
              <Card>
                <CardHeader title={getMessage('label.pros.and.cons')} />
                <CardContent>
                  <ReadOnlyTextField text={getMessage('label.pros')}>
                    {pageData.applicationDecision?.pros &&
                      !HtmlSanitizer.isEmptyLineHtmlParagraph(pageData.applicationDecision.pros) && (
                        <HtmlRenderer html={pageData.applicationDecision?.pros} />
                      )}
                  </ReadOnlyTextField>
                  <ReadOnlyTextField text={getMessage('label.cons')} isLast={true}>
                    {pageData.applicationDecision?.cons &&
                      !HtmlSanitizer.isEmptyLineHtmlParagraph(pageData.applicationDecision.cons) && (
                        <HtmlRenderer html={pageData.applicationDecision?.cons} />
                      )}
                  </ReadOnlyTextField>
                </CardContent>
              </Card>

              {/* information on the decision  */}
              <Card>
                <CardHeader title={getMessage('label.information.decision')} />
                <CardContent>
                  <ReadOnlySelection text={getMessage('label.exclusion.criteria')}>
                    {pageData.exclusionCriteria && pageData.exclusionCriteria?.length > 0 && (
                      <S.List.ReadOnly>
                        {pageData.exclusionCriteria.map((ec: any) => (
                          <ListItem key={ec}>
                            <ListItemIcon>
                              <CheckIcon />
                            </ListItemIcon>
                            <ListItemText primary={ec} />
                          </ListItem>
                        ))}
                      </S.List.ReadOnly>
                    )}
                  </ReadOnlySelection>

                  <ReadOnlySelection text={getMessage('label.quality.criteria')}>
                    {pageData.qualityCriteria && pageData.qualityCriteria?.length > 0 && (
                      <S.List.ReadOnly>
                        {pageData.qualityCriteria.map((qc: any) => (
                          <ListItem key={qc}>
                            <ListItemIcon>
                              <CheckIcon />
                            </ListItemIcon>
                            <ListItemText primary={qc} />
                          </ListItem>
                        ))}
                      </S.List.ReadOnly>
                    )}
                  </ReadOnlySelection>

                  <ReadOnlyTextField text={getMessage('label.decision.letter')}>
                    {pageData.applicationDecision?.decisionLetter &&
                      !HtmlSanitizer.isEmptyLineHtmlParagraph(pageData.applicationDecision.decisionLetter) && (
                        <HtmlRenderer html={pageData.applicationDecision?.decisionLetter} />
                      )}
                  </ReadOnlyTextField>

                  <ReadOnlySelection text={getMessage('label.gfch.responsible')} isLast={true}>
                    {pageData.gfchResponsible}
                  </ReadOnlySelection>
                </CardContent>
              </Card>

              {/* final decision section */}
              <Section id="final-decision">
                <Card>
                  <CardHeader title={getMessage('label.final.decision')} />
                  <CardContent>
                    <ReadOnlySelection text={getMessage('label.decision')} isLast={true}>
                      {pageData.applicationDecision?.finalDecision &&
                        getMessage(`label.recommendation.${pageData.applicationDecision.finalDecision}`)}
                    </ReadOnlySelection>
                  </CardContent>
                </Card>
              </Section>
            </Stack>
          </Section>
        )}
      </PageLayout>
    </ScreenLayout>
  )
}
